import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridRowSelectionModel,
  GridToolbar,
} from '@mui/x-data-grid';

import { Autocomplete, Select } from '~/components/Common/Forms/fields';

import { BEDBANK_PROPERTY_DOMAINS_ENUM, BEDBANK_PROPERTY_STATUSES_ENUM, SUPPLIER_NAMES } from '~/consts/bedbank';
import { countriesOptions } from '~/consts/bedbankFilterOptions';

import {
  useApprovePotentialPropertyMappings,
  useEnqueuePotentialPropertyMappingAvailabilityScores,
  usePotentialPropertyMappingProgressMultiStatuses,
  usePotentialPropertyMappings,
  useRejectPotentialPropertyMappings,
} from '~/hooks/apis/bedbank/usePotentialPropertyMappings';

import { formatDateISO } from '~/services/TimeService';

import { toTitleCase } from '~/utils/toTitleCase';

import { partitionBulkAction } from './bulkActionPartition';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const MAX_COMPUTE_GROUPS_PER_RUN = 10;
const FAST_POLL_INTERVAL_MS = 2000;
const SLOW_POLL_INTERVAL_MS = 5000;
const FAST_POLL_ATTEMPTS = 5;
const MAX_POLL_ERROR_RETRIES = 3;

const CONFIDENCE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
];

const SUPPLIER_MAPPED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const STAR_RATING_OPTIONS = [
  { value: '', label: 'All' },
  { value: '3', label: '3' },
  { value: '3.5', label: '3.5' },
  { value: '4', label: '4' },
  { value: '4.5', label: '4.5' },
  { value: '5', label: '5' },
];

const MAPPING_STATE_LABELS: Record<App.Bedbank.PotentialPropertyMappingState, string> = {
  unmapped: 'Unmapped',
  mapped: 'Mapped',
  'manually-mapped': 'Manually Mapped',
  'auto-mapped': 'Auto-mapped',
};

const MAPPING_STATE_CHIP_COLORS: Record<App.Bedbank.PotentialPropertyMappingState, 'default' | 'success'> = {
  unmapped: 'default',
  mapped: 'success',
  'manually-mapped': 'success',
  'auto-mapped': 'success',
};

type CountryOption = {
  label: string;
  value: string;
};

interface FilterFormValues {
  supplier: string;
  domain: string;
  status: string;
  region: Array<CountryOption>;
  starRating: string;
  confidence: string;
  supplierMapped: string;
}

const FILTER_DEFAULTS: FilterFormValues = {
  supplier: '',
  domain: '',
  status: '',
  region: [],
  starRating: '',
  confidence: '',
  supplierMapped: '',
};

const getMappingRowId = (row: App.Bedbank.PotentialPropertyMapping): GridRowId => row.id;

function BooleanIcon({ value }: { value?: boolean | null }) {
  if (value == null) return <Typography variant="body2">-</Typography>;
  return value ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />;
}

const MAPPING_STATE_FILTERS = ['all', 'unmapped', 'manually-mapped', 'auto-mapped'] as const;

const FILTER_LABELS: Record<(typeof MAPPING_STATE_FILTERS)[number], string> = {
  all: 'All',
  unmapped: 'Unmapped',
  'manually-mapped': 'Manually Mapped',
  'auto-mapped': 'Auto-mapped',
};
type MappingStateFilter = (typeof MAPPING_STATE_FILTERS)[number];

interface PendingSwapRecommendationJob {
  groupKey: string;
  progressStatusId: string;
}

export default function PotentialPropertyMappingsPage() {
  const [selectedMappingIds, setSelectedMappingIds] = useState<GridRowSelectionModel>([]);
  const [pendingActionIds, setPendingActionIds] = useState<Array<string>>([]);
  const [hasAlternativeCandidateById, setHasAlternativeCandidateById] = useState<Record<string, boolean>>({});
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [pendingSwapRecommendationJobs, setPendingSwapRecommendationJobs] = useState<
    Array<PendingSwapRecommendationJob>
  >([]);
  const [processingSwapGroupKeys, setProcessingSwapGroupKeys] = useState<Array<string>>([]);
  const [stateFilter, setStateFilter] = useState<MappingStateFilter>('unmapped');
  const [addressRow, setAddressRow] = useState<App.Bedbank.PotentialPropertyMapping | null>(null);
  const pollAttemptRef = useRef(0);
  const pollErrorRetryRef = useRef(0);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, reset } = useForm<FilterFormValues>({
    defaultValues: FILTER_DEFAULTS,
  });

  const [appliedFilters, setAppliedFilters] = useState<App.Bedbank.PotentialPropertyMappingsFilters>({});

  const requestFilters = useMemo<App.Bedbank.PotentialPropertyMappingsFilters>(
    () => ({
      ...appliedFilters,
      mappingState: stateFilter === 'all' ? undefined : stateFilter,
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    }),
    [appliedFilters, paginationModel.page, paginationModel.pageSize, stateFilter],
  );

  const {
    data: mappingsResponse,
    isLoading,
    isFetching,
    error,
    refetch: refetchPotentialPropertyMappings,
  } = usePotentialPropertyMappings(requestFilters);

  const approveMutation = useApprovePotentialPropertyMappings();
  const rejectMutation = useRejectPotentialPropertyMappings();
  const enqueueAvailabilityScoreMutation = useEnqueuePotentialPropertyMappingAvailabilityScores();
  const progressMultiStatusesMutation = usePotentialPropertyMappingProgressMultiStatuses();
  const enqueueSwapRecommendationJobs = enqueueAvailabilityScoreMutation.mutateAsync;
  const pollSwapRecommendationStatusesRef = useRef(progressMultiStatusesMutation.mutateAsync);
  pollSwapRecommendationStatusesRef.current = progressMultiStatusesMutation.mutateAsync;

  const mappings = useMemo(() => mappingsResponse?.result ?? [], [mappingsResponse?.result]);
  const totalMappings = mappingsResponse?.total ?? 0;
  const mappingsById = useMemo(() => new Map(mappings.map((mapping) => [mapping.id, mapping])), [mappings]);
  const processingSwapGroupKeySet = useMemo(() => new Set(processingSwapGroupKeys), [processingSwapGroupKeys]);
  const isSwapRecommendationProcessing = pendingSwapRecommendationJobs.length > 0;
  const isSupplierMappedYesFilter = appliedFilters.supplierAlreadyMapped === true;
  const selectedMappingIdStrings = useMemo(() => selectedMappingIds.map(String), [selectedMappingIds]);
  const selectedMappingEligibility = useMemo(
    () =>
      selectedMappingIdStrings.map((mappingId) => {
        const fromSelectedRows = hasAlternativeCandidateById[mappingId];
        const fromCurrentPage = mappingsById.get(mappingId)?.hasAlternativeCandidate;
        return {
          mappingId,
          hasAlternativeCandidate: fromSelectedRows ?? fromCurrentPage ?? false,
        };
      }),
    [hasAlternativeCandidateById, mappingsById, selectedMappingIdStrings],
  );
  const selectedEligibleMappingIds = useMemo(
    () => selectedMappingEligibility.filter((row) => row.hasAlternativeCandidate).map((row) => row.mappingId),
    [selectedMappingEligibility],
  );
  const selectedIneligibleCount = useMemo(
    () => selectedMappingEligibility.length - selectedEligibleMappingIds.length,
    [selectedEligibleMappingIds.length, selectedMappingEligibility.length],
  );

  useEffect(() => {
    if (!isSupplierMappedYesFilter) {
      setHasAlternativeCandidateById((current) => (Object.keys(current).length > 0 ? {} : current));
      return;
    }

    setHasAlternativeCandidateById((current) => {
      const currentPageById = new Map(
        mappings.map((mapping) => [mapping.id, mapping.hasAlternativeCandidate === true]),
      );
      const idsToKeep = new Set<string>(selectedMappingIdStrings);
      mappings.forEach((mapping) => idsToKeep.add(mapping.id));

      const next: Record<string, boolean> = {};
      idsToKeep.forEach((id) => {
        if (currentPageById.has(id)) {
          next[id] = currentPageById.get(id) ?? false;
          return;
        }
        if (id in current) {
          next[id] = current[id];
        }
      });

      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(next);
      const hasChanged = currentKeys.length !== nextKeys.length || nextKeys.some((key) => current[key] !== next[key]);

      return hasChanged ? next : current;
    });
  }, [isSupplierMappedYesFilter, mappings, selectedMappingIdStrings]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        `Failed to load potential property mappings: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, error]);

  const onSubmit = useCallback((values: FilterFormValues) => {
    setAppliedFilters({
      supplier: values.supplier || undefined,
      masterPropertyDomain: (values.domain as App.Bedbank.PotentialPropertyMappingDomain) || undefined,
      masterPropertyStatus: values.status || undefined,
      masterPropertyRegion: values.region.length === 1 ? values.region[0].value : undefined,
      masterPropertyStarRating: values.starRating ? Number(values.starRating) : undefined,
      confidence: (values.confidence as App.Bedbank.PotentialPropertyMappingConfidence) || undefined,
      supplierAlreadyMapped:
        values.supplierMapped === 'true' ? true : values.supplierMapped === 'false' ? false : undefined,
    });
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setSelectedMappingIds([]);
  }, []);

  const handleClearFilters = useCallback(() => {
    reset(FILTER_DEFAULTS);
    setAppliedFilters({});
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setSelectedMappingIds([]);
  }, [reset]);

  const scrollResultsIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      const hasPageChanged =
        nextPaginationModel.page !== paginationModel.page || nextPaginationModel.pageSize !== paginationModel.pageSize;

      setPaginationModel(nextPaginationModel);

      if (hasPageChanged) {
        scrollResultsIntoView();
      }
    },
    [paginationModel.page, paginationModel.pageSize, scrollResultsIntoView],
  );

  const handleComputeSwapRecommendation = useCallback(async () => {
    if (isSwapRecommendationProcessing) {
      enqueueSnackbar('Swap recommendation is currently processing. Please wait for the current run to finish.', {
        variant: 'info',
      });
      return;
    }

    if (!selectedEligibleMappingIds.length) {
      if (selectedIneligibleCount > 0) {
        enqueueSnackbar('None of the selected mappings have alternative candidates to score.', {
          variant: 'info',
        });
      }
      return;
    }

    if (!isSupplierMappedYesFilter) return;

    const submittedIds = selectedEligibleMappingIds;
    const uniqueGroupKeys = new Set<string>();

    submittedIds.forEach((mappingId) => {
      const mapping = mappingsById.get(mappingId);
      if (mapping?.bedbankPropertyId && mapping?.supplier) {
        uniqueGroupKeys.add(`${mapping.bedbankPropertyId}:${mapping.supplier}`);
      } else {
        // Keep unknown selections conservative when rows are no longer present in the current page.
        uniqueGroupKeys.add(`unknown:${mappingId}`);
      }
    });

    if (uniqueGroupKeys.size > MAX_COMPUTE_GROUPS_PER_RUN) {
      enqueueSnackbar(`You can compute up to ${MAX_COMPUTE_GROUPS_PER_RUN} mappings at a time.`, {
        variant: 'error',
      });
      return;
    }

    setPendingActionIds(submittedIds);
    try {
      if (selectedIneligibleCount > 0) {
        enqueueSnackbar(`Skipping ${selectedIneligibleCount} selected mapping(s) with no alternatives.`, {
          variant: 'info',
        });
      }

      const response = await enqueueSwapRecommendationJobs(submittedIds);
      const scoreJobs = response.result?.scores ?? [];

      if (scoreJobs.length === 0) {
        enqueueSnackbar('No swap recommendation jobs were queued for the selected mappings.', { variant: 'warning' });
        return;
      }

      const newJobs = scoreJobs.map((job) => ({
        groupKey: `${job.bedbankPropertyId}:${job.supplier}`,
        progressStatusId: job.progressStatusId,
      }));
      const newGroupKeys = newJobs.map((job) => job.groupKey);

      setProcessingSwapGroupKeys((current) => [...new Set([...current, ...newGroupKeys])]);
      setPendingSwapRecommendationJobs((current) => {
        const jobsByGroupKey = new Map(current.map((job) => [job.groupKey, job]));
        newJobs.forEach((job) => jobsByGroupKey.set(job.groupKey, job));
        return [...jobsByGroupKey.values()];
      });

      enqueueSnackbar(`Queued swap recommendation for ${scoreJobs.length} mapping group(s).`, { variant: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      enqueueSnackbar(`Failed to queue swap recommendation jobs: ${message}`, { variant: 'error' });
    } finally {
      setPendingActionIds([]);
    }
  }, [
    enqueueSnackbar,
    enqueueSwapRecommendationJobs,
    isSwapRecommendationProcessing,
    isSupplierMappedYesFilter,
    mappingsById,
    selectedEligibleMappingIds,
    selectedIneligibleCount,
  ]);

  useEffect(() => {
    if (pendingSwapRecommendationJobs.length === 0) {
      pollAttemptRef.current = 0;
      pollErrorRetryRef.current = 0;
      return;
    }

    let isCancelled = false;
    let timeoutId: number | undefined;

    const pollSwapRecommendations = async () => {
      try {
        const progressResponse = await pollSwapRecommendationStatusesRef.current(
          pendingSwapRecommendationJobs.map((job) => job.progressStatusId),
        );
        if (isCancelled) return;
        pollErrorRetryRef.current = 0;

        const statuses = progressResponse.result?.statuses ?? [];
        const statusesById = new Map(statuses.map((status) => [status.id, status]));
        const completedJobs: Array<PendingSwapRecommendationJob> = [];
        const failedJobs: Array<{ job: PendingSwapRecommendationJob; message: string }> = [];

        pendingSwapRecommendationJobs.forEach((job) => {
          const status = statusesById.get(job.progressStatusId);
          if (!status) return;
          if (status.state === 'completed') {
            completedJobs.push(job);
            return;
          }
          if (status.state === 'failed') {
            failedJobs.push({
              job,
              message: status.message ?? 'Unknown error',
            });
          }
        });

        if (completedJobs.length > 0 || failedJobs.length > 0) {
          const terminalJobIds = new Set(
            [...completedJobs, ...failedJobs.map(({ job }) => job)].map((job) => job.progressStatusId),
          );
          const terminalGroupKeys = new Set([
            ...completedJobs.map((job) => job.groupKey),
            ...failedJobs.map(({ job }) => job.groupKey),
          ]);

          setPendingSwapRecommendationJobs((current) =>
            current.filter((job) => !terminalJobIds.has(job.progressStatusId)),
          );
          setProcessingSwapGroupKeys((current) => current.filter((groupKey) => !terminalGroupKeys.has(groupKey)));

          await refetchPotentialPropertyMappings();

          if (completedJobs.length > 0) {
            enqueueSnackbar(`Computed swap recommendation for ${completedJobs.length} mapping group(s).`, {
              variant: 'success',
            });
          }

          if (failedJobs.length > 0) {
            enqueueSnackbar(`Failed to compute ${failedJobs.length} mapping group(s): ${failedJobs[0].message}`, {
              variant: 'error',
            });
          }

          pollAttemptRef.current = 0;
          return;
        }

        pollAttemptRef.current += 1;
        const nextPollInterval =
          pollAttemptRef.current > FAST_POLL_ATTEMPTS ? SLOW_POLL_INTERVAL_MS : FAST_POLL_INTERVAL_MS;
        timeoutId = window.setTimeout(() => {
          void pollSwapRecommendations();
        }, nextPollInterval);
      } catch (pollError) {
        if (isCancelled) return;
        const message = pollError instanceof Error ? pollError.message : 'Unknown error';
        const nextErrorRetry = pollErrorRetryRef.current + 1;
        pollErrorRetryRef.current = nextErrorRetry;

        if (nextErrorRetry >= MAX_POLL_ERROR_RETRIES) {
          const exhaustedProgressStatusIds = new Set(pendingSwapRecommendationJobs.map((job) => job.progressStatusId));
          const exhaustedGroupKeys = new Set(pendingSwapRecommendationJobs.map((job) => job.groupKey));
          setPendingSwapRecommendationJobs((current) =>
            current.filter((job) => !exhaustedProgressStatusIds.has(job.progressStatusId)),
          );
          setProcessingSwapGroupKeys((current) => current.filter((groupKey) => !exhaustedGroupKeys.has(groupKey)));
          pollAttemptRef.current = 0;
          pollErrorRetryRef.current = 0;
          enqueueSnackbar(
            `Stopped polling swap recommendation status after ${MAX_POLL_ERROR_RETRIES} failed attempts: ${message}`,
            { variant: 'error' },
          );
          return;
        }

        pollAttemptRef.current += 1;
        timeoutId = window.setTimeout(() => {
          void pollSwapRecommendations();
        }, SLOW_POLL_INTERVAL_MS);
      }
    };

    void pollSwapRecommendations();

    return () => {
      isCancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [enqueueSnackbar, pendingSwapRecommendationJobs, refetchPotentialPropertyMappings]);

  const handleBulkAction = useCallback(
    async (action: 'approve' | 'reject') => {
      if (!selectedMappingIds.length) {
        return;
      }

      const { ambiguousIds, submittedIds } = partitionBulkAction(action, selectedMappingIds, mappingsById);
      setPendingActionIds(submittedIds);
      setConfirmAction(null);

      if (ambiguousIds.length > 0) {
        enqueueSnackbar(
          `Skipping ${ambiguousIds.length} mapping(s) whose names differ only by a number. Approve those individually.`,
          { variant: 'warning' },
        );
      }

      if (submittedIds.length === 0) {
        setPendingActionIds([]);
        return;
      }

      try {
        const mutation = action === 'approve' ? approveMutation : rejectMutation;
        const results = await mutation.mutateAsync(submittedIds);

        const succeededIds: Array<string> = [];
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            succeededIds.push(submittedIds[index]);
          } else {
            const message = result.reason instanceof Error ? result.reason.message : 'Unknown error';
            enqueueSnackbar(`Failed to ${action} mapping ${submittedIds[index]}: ${message}`, { variant: 'error' });
          }
        });

        if (succeededIds.length > 0) {
          const label = action === 'approve' ? 'Approved' : 'Rejected';
          enqueueSnackbar(`${label} ${succeededIds.length} potential mapping(s).`, { variant: 'success' });
        }

        setSelectedMappingIds((prev) => prev.filter((id) => !succeededIds.includes(String(id))));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        enqueueSnackbar(`Bulk ${action} failed: ${message}`, { variant: 'error' });
      } finally {
        setPendingActionIds([]);
      }
    },
    [enqueueSnackbar, approveMutation, rejectMutation, selectedMappingIds, mappingsById],
  );

  const isBusy = approveMutation.isPending || rejectMutation.isPending || enqueueAvailabilityScoreMutation.isPending;

  const columns: Array<GridColDef<App.Bedbank.PotentialPropertyMapping>> = useMemo(
    () => [
      {
        field: 'bedbankPropertyName',
        headerName: 'Bedbank Property',
        flex: 2,
        minWidth: 240,
        display: 'flex',
        renderCell: (params) => {
          const name = params.row.bedbankPropertyName ?? '-';
          const id = params.row.bedbankPropertyId;
          if (id) {
            return (
              <Link href={`/bedbank/properties/${id}`} target="_blank" rel="noreferrer" underline="hover">
                {name}
              </Link>
            );
          }
          return <Typography variant="body2">{name}</Typography>;
        },
      },
      {
        field: 'supplier',
        headerName: 'Supplier',
        minWidth: 100,
        flex: 1,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'masterPropertyDomain',
        headerName: 'Domain',
        minWidth: 80,
        flex: 0.7,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'masterPropertyStatus',
        headerName: 'Status',
        minWidth: 120,
        flex: 1,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'supplierPropertyName',
        headerName: 'Supplier Property',
        minWidth: 220,
        flex: 2,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'supplierPropertyId',
        headerName: 'Supplier ID',
        minWidth: 180,
        flex: 1,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'masterPropertyRegion',
        headerName: 'Region',
        minWidth: 120,
        flex: 1,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'masterPropertyStarRating',
        headerName: 'Stars',
        minWidth: 80,
        flex: 0.5,
        display: 'flex',
        valueGetter: (value) => (value != null ? value : '-'),
      },
      {
        field: 'matchScore',
        headerName: 'Score',
        minWidth: 80,
        flex: 1,
        display: 'flex',
        valueGetter: (value, row) => value ?? row.confidence ?? '-',
      },
      {
        field: 'distanceKm',
        headerName: 'Distance (km)',
        minWidth: 120,
        flex: 1,
        display: 'flex',
        valueGetter: (value) => (value != null ? value : '-'),
      },
      {
        field: 'supplierVervotechId',
        headerName: 'Vervotech ID',
        minWidth: 120,
        flex: 1,
        display: 'flex',
        valueGetter: (value) => value ?? '-',
      },
      {
        field: 'vervotechMatch',
        headerName: 'Vervotech Match',
        minWidth: 100,
        flex: 0.8,
        display: 'flex',
        renderCell: (params) => <BooleanIcon value={params.row.vervotechMatch} />,
      },
      {
        field: 'supplierAlreadyMapped',
        headerName: 'Supplier Mapped',
        minWidth: 100,
        flex: 0.8,
        display: 'flex',
        renderCell: (params) => <BooleanIcon value={params.row.supplierAlreadyMapped} />,
      },
      {
        field: 'unitNumberAmbiguity',
        headerName: 'Unit number',
        minWidth: 130,
        flex: 1,
        display: 'flex',
        renderCell: (params) =>
          params.row.unitNumberAmbiguity ? (
            <Chip label="differs" color="warning" size="small" variant="filled" />
          ) : (
            <Chip label="-" size="small" variant="outlined" />
          ),
      },
      ...(isSupplierMappedYesFilter
        ? [
            {
              field: 'hasAlternativeCandidate',
              headerName: 'Has Alternatives',
              minWidth: 140,
              flex: 1,
              display: 'flex',
              renderCell: (params) => (
                <Chip
                  label={params.row.hasAlternativeCandidate ? 'yes' : 'no'}
                  color={params.row.hasAlternativeCandidate ? 'success' : 'default'}
                  size="small"
                  variant="filled"
                />
              ),
            } as GridColDef<App.Bedbank.PotentialPropertyMapping>,
            {
              field: 'swapRecommended',
              headerName: 'Swap Recommended',
              minWidth: 150,
              flex: 1,
              display: 'flex',
              renderCell: (params) => {
                const groupKey =
                  params.row.bedbankPropertyId && params.row.supplier
                    ? `${params.row.bedbankPropertyId}:${params.row.supplier}`
                    : null;
                const isProcessing = groupKey != null && processingSwapGroupKeySet.has(groupKey);

                if (isProcessing) {
                  return <Chip label="processing" color="warning" size="small" variant="filled" />;
                }

                if (params.row.swapRecommended == null) {
                  return <Typography variant="body2">-</Typography>;
                }

                return (
                  <Chip
                    label={params.row.swapRecommended ? 'yes' : 'no'}
                    color={params.row.swapRecommended ? 'success' : 'default'}
                    size="small"
                    variant="filled"
                  />
                );
              },
            } as GridColDef<App.Bedbank.PotentialPropertyMapping>,
          ]
        : []),
      {
        field: 'addresses',
        headerName: 'Addresses',
        minWidth: 110,
        flex: 0.7,
        display: 'flex',
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const hasMaster = !!params.row.masterPropertyAddress;
          const hasSupplier = !!params.row.supplierPropertyAddress;
          if (!hasMaster && !hasSupplier) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Button size="small" variant="text" onClick={() => setAddressRow(params.row)}>
              Compare
            </Button>
          );
        },
      },
      {
        field: 'mappingState',
        headerName: 'Mapping State',
        minWidth: 140,
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          const state = params.row.mappingState;
          if (!state) {
            return <Typography variant="body2">-</Typography>;
          }
          const color = MAPPING_STATE_CHIP_COLORS[state] ?? 'default';
          const label = MAPPING_STATE_LABELS[state] ?? state;
          return <Chip label={label} color={color} size="small" variant="filled" />;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 120,
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          const value = params.row.createdAt;
          if (!value) {
            return <Typography variant="body2">-</Typography>;
          }
          return <Typography variant="body2">{formatDateISO(value)}</Typography>;
        },
      },
    ],
    [isSupplierMappedYesFilter, processingSwapGroupKeySet],
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid spacing={2} container mb={2}>
            <Grid size={{ md: 3 }}>
              <Select control={control} name="supplier" muiLabel="Supplier">
                {['', ...SUPPLIER_NAMES].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s ? toTitleCase(s) : 'All'}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="domain" muiLabel="Domain">
                {['', ...BEDBANK_PROPERTY_DOMAINS_ENUM].map((d) => (
                  <MenuItem key={d} value={d}>
                    {d || 'All'}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="status" muiLabel="Status">
                {['', ...BEDBANK_PROPERTY_STATUSES_ENUM].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s || 'All'}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 3 }}>
              <Autocomplete
                multiple
                control={control}
                getOptionLabel={(option: CountryOption) => option.label}
                name="region"
                muiLabel="Region (Country)"
                options={countriesOptions}
              />
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="starRating" muiLabel="Star Rating">
                {STAR_RATING_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="confidence" muiLabel="Confidence">
                {CONFIDENCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="supplierMapped" muiLabel="Supplier mapped">
                {SUPPLIER_MAPPED_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Stack direction="row" spacing={1} sx={{ height: '100%' }} alignItems="flex-end">
                <Button type="submit" variant="contained">
                  Search
                </Button>
                <Button variant="text" onClick={handleClearFilters}>
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value as MappingStateFilter);
              setPaginationModel((current) => ({ ...current, page: 0 }));
              setSelectedMappingIds([]);
            }}
          >
            {MAPPING_STATE_FILTERS.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={FILTER_LABELS[value]}
              />
            ))}
          </RadioGroup>
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              color="primary"
              onClick={handleComputeSwapRecommendation}
              disabled={
                selectedEligibleMappingIds.length === 0 ||
                isBusy ||
                isSwapRecommendationProcessing ||
                !isSupplierMappedYesFilter
              }
            >
              {enqueueAvailabilityScoreMutation.isPending
                ? `Queueing ${pendingActionIds.length}...`
                : isSwapRecommendationProcessing
                  ? 'Computing...'
                  : 'Compute Swap Recommendation'}
            </Button>
            <Button
              variant="text"
              color="error"
              onClick={() => setConfirmAction('reject')}
              disabled={selectedMappingIds.length === 0 || isBusy}
            >
              {rejectMutation.isPending ? `Rejecting ${pendingActionIds.length}...` : 'Reject'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setConfirmAction('approve')}
              disabled={selectedMappingIds.length === 0 || isBusy}
            >
              {approveMutation.isPending ? `Approving ${pendingActionIds.length}...` : 'Approve'}
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle>Confirm Bulk {confirmAction === 'approve' ? 'Approval' : 'Rejection'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmAction} {selectedMappingIds.length} potential property mapping(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} variant="text">
            Cancel
          </Button>
          <Button
            onClick={() => confirmAction && handleBulkAction(confirmAction)}
            variant="contained"
            color={confirmAction === 'approve' ? 'primary' : 'error'}
          >
            {confirmAction === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box ref={resultsRef} sx={{ width: '100%', minWidth: 0 }}>
        <DataGrid
          autoHeight
          rows={mappings}
          columns={columns}
          getRowId={getMappingRowId}
          checkboxSelection
          pagination
          paginationMode="server"
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalMappings}
          loading={isLoading || isFetching}
          isRowSelectable={(params) => !isSupplierMappedYesFilter || params.row.hasAlternativeCandidate === true}
          onRowSelectionModelChange={(newSelection) => setSelectedMappingIds(newSelection)}
          rowSelectionModel={selectedMappingIds}
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableDensitySelector
          disableColumnSelector
          slots={{
            toolbar: GridToolbar,
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
              overflow: 'visible',
            },
          }}
        />
      </Box>

      <Dialog open={addressRow !== null} onClose={() => setAddressRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Address Comparison</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Master Property Address
              </Typography>
              <Typography variant="body2">{addressRow?.masterPropertyAddress || 'Not available'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Supplier Property Address
              </Typography>
              <Typography variant="body2">{addressRow?.supplierPropertyAddress || 'Not available'}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressRow(null)} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
